import React, { type FC, type ReactElement } from 'react'
import {
  AppBar,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  type SvgIconProps,
  Toolbar,
  Typography
} from '@mui/material'
import ChevronRight from '@mui/icons-material/ChevronRight'
import List from '@mui/icons-material/List'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuIcon from '@mui/icons-material/Menu'
import TermsIcon from '@mui/icons-material/Description'
import HelpIcon from '@mui/icons-material/Help'
import Company from '@mui/icons-material/BusinessCenter'
import CompareArrows from '@mui/icons-material/CompareArrows'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import HeaderBackground from '../../../assets/header-bgr.svg'
import HydronetLogo from '../../../assets/logo-header.svg'
import LanguageSelector from '../language-selector/LanguageSelector'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { LogoutSuccessAction } from '../../../redux/auth/auth-actions'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { type RootState } from '../../../redux/store'

const headerBgStyle = {
  backgroundImage: `url(${HeaderBackground})`,
  backgroundColor: '#000000',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover'
}

const hamburgerMenuStyle = {
  color: '#ffffff',
  fontSize: '1.9rem'
}

type NavigateActionType = 'navigate' | 'openFile'

interface AppMenuItem {
  labelKey: string
  icon: React.ReactElement<SvgIconProps>
  adminOnly: boolean
  path: string
  navigateActionType: NavigateActionType
}

/**
 * Menu items.
 */
const appMenuItems: AppMenuItem[] = [
  // METERS
  {
    labelKey: 'bottom_bar.meters',
    icon: <List/>,
    adminOnly: false,
    navigateActionType: 'navigate',
    path: '/dashboard/meters'
  },

  // SETTINGS
  {
    labelKey: 'bottom_bar.settings',
    icon: <SettingsIcon/>,
    adminOnly: false,
    navigateActionType: 'navigate',
    path: '/dashboard/settings'
  },

  // INTEGRATIONS
  {
    labelKey: 'bottom_bar.integrations',
    icon: <CompareArrows/>,
    adminOnly: true,
    navigateActionType: 'navigate',
    path: '/dashboard/integrations'
  },

  // CUSTOMERS
  {
    labelKey: 'bottom_bar.customers',
    icon: <Company/>,
    adminOnly: true,
    navigateActionType: 'navigate',
    path: '/dashboard/customers'
  },

  // ACTIVATION
  {
    labelKey: 'bottom_bar.activation',
    icon: <SettingsIcon/>,
    adminOnly: true,
    navigateActionType: 'navigate',
    path: '/dashboard/devices_for_provision'
  },

  // TERMS
  {
    labelKey: 'bottom_bar.terms',
    icon: <TermsIcon/>,
    adminOnly: false,
    navigateActionType: 'openFile',
    path: '/docs/hydronet-kayttoehdot.pdf'
  },

  // TERMS
  {
    labelKey: 'bottom_bar.help',
    icon: <HelpIcon/>,
    adminOnly: false,
    navigateActionType: 'openFile',
    path: '/docs/Hydronet_ohje_v.2.5.1.pdf'
  },

  // NOTICES
  {
    labelKey: 'notices.notices',
    icon: <TextSnippetIcon/>,
    adminOnly: false,
    navigateActionType: 'navigate',
    path: '/dashboard/notices'
  }
]

/**
 * App Header component-
 */
export const Header: FC = () => {
  const dispatch = useAppDispatch()
  const username = useAppSelector((state: RootState): string => state.auth.username)

  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const onMenuOpen = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(e.currentTarget)
  }

  const onMenuClose = (): void => {
    setAnchorEl(null)
  }

  return (
        <AppBar style={headerBgStyle}>
            <Toolbar style={{ margin: '0 20% 0 20%' }}>
                <Grid
                    container
                    justifyContent={'space-between'}
                    wrap={'nowrap'}
                >
                    {/* LEFT SIDE */}
                    <Grid item>
                        <IconButton edge="start" onClick={onMenuOpen} size="large">
                            <MenuIcon style={hamburgerMenuStyle}></MenuIcon>
                        </IconButton>
                        <Menu
                            open={Boolean(anchorEl)}
                            onClose={onMenuClose}
                            anchorEl={anchorEl}
                            disableScrollLock
                        >
                            <AppMenuItems onMenuItemClicked={onMenuClose}/>
                        </Menu>
                    </Grid>

                    {/* LOGO */}
                    <Grid item style={{
                      marginLeft: '25%',
                      display: 'flex',
                      justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                        <img src={HydronetLogo} alt="hydronet-logo"/>
                    </Grid>

                    {/* RIGHT SIDE */}
                    <Grid item>
                        <Grid container wrap={'nowrap'} alignItems={'center'} style={{ height: '100%' }}>
                            <Grid item style={{ marginRight: '2.5em' }}>
                                <Typography style={{ color: '#ffffff' }} variant="h6">
                                    {username}
                                </Typography>
                            </Grid>
                            <Grid item style={{ marginRight: '2.5em', minWidth: '5em' }}>
                                <LanguageSelector/>
                            </Grid>

                            <Grid item style={{ marginRight: '2.5em' }}>
                                <Button
                                    endIcon={<ChevronRight/>}
                                    onClick={() => dispatch(LogoutSuccessAction())}
                                    style={{ color: '#ffffff', fontSize: '1rem' }}
                                >
                                    {t('landing.logout')}
                                </Button>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </Toolbar>
        </AppBar>
  )
}

/* Child Components */

interface AppMenuItemsProps {
  onMenuItemClicked: () => void
}

/**
 * Application tabs component.
 */
function AppMenuItems({ onMenuItemClicked }: Readonly<AppMenuItemsProps>): ReactElement {
  const navigate = useNavigate()
  const isAdmin = useAppSelector((state: RootState): boolean => state.auth.isAdmin)

  const onMenuItemClick = (appMenuItem: AppMenuItem): void => {
    if (appMenuItem.navigateActionType === 'navigate') {
      navigate(appMenuItem.path)
    } else if (appMenuItem.navigateActionType === 'openFile') {
      window.open(appMenuItem.path)
    }

    onMenuItemClicked()
  }

  const visibleMenuItems = appMenuItems.filter((appMenuItem: AppMenuItem) => !appMenuItem.adminOnly || isAdmin)

  return (
        <>
            {visibleMenuItems.map((appMenuItem: AppMenuItem) =>
                <AppMenuItemRow key={appMenuItem.labelKey} menuItem={appMenuItem} onMenuItemClick={onMenuItemClick}/>
            )
            }
        </>
  )
}

interface AppMenuItemRowProps {
  menuItem: AppMenuItem
  onMenuItemClick: (menuItem: AppMenuItem) => void
}

function AppMenuItemRow({ menuItem, onMenuItemClick }: Readonly<AppMenuItemRowProps>): ReactElement {
  const { t } = useTranslation()

  return (
        <MenuItem key={menuItem.labelKey} onClick={() => {
          onMenuItemClick(menuItem)
        }}>
            <ListItemIcon>
                {menuItem.icon}
            </ListItemIcon>
            <Typography>
                {t(menuItem.labelKey)}
            </Typography>
        </MenuItem>
  )
}
